import { createFileRoute } from "@tanstack/react-router";
import { Plus, Minus } from "lucide-react";
import { sanityClient } from "@/lib/sanity";
import { useLocale, pickLocalized, tr, t, DEFAULT_LOCALE } from "@/lib/i18n";
import { pageMeta, faqLd, JsonLd } from "@/lib/seo";

const QUERY = `{
  "page": *[_type == "faqPage"][0]{ eyebrow, title, seo },
  "items": *[_type == "faq"] | order(order asc){ _id, question, answer }
}`;

export const Route = createFileRoute("/faq")({
  component: FaqPage,
  loader: () => sanityClient.fetch<any>(QUERY),
  head: ({ loaderData }) =>
    pageMeta({
      title: pickLocalized(loaderData?.page?.seo?.metaTitle, DEFAULT_LOCALE) || "FAQ — Matchamind",
      description: pickLocalized(loaderData?.page?.seo?.metaDescription, DEFAULT_LOCALE) || undefined,
      path: "/faq",
    }),
});

function FaqPage() {
  const locale = useLocale();
  const data = Route.useLoaderData();

  const eyebrow = pickLocalized(data?.page?.eyebrow, locale);
  const title = pickLocalized(data?.page?.title, locale) || tr(t.nav.faq, locale);

  return (
    <div>
      <section className="mx-auto flex w-full max-w-[1040px] flex-col items-start gap-6 px-6 py-16 md:px-10 md:py-[100px]">
        {eyebrow && (
          <div className="inline-flex items-center justify-center rounded-[43px] bg-secondary-300 px-2 py-1">
            <span className="font-sans text-[14px] leading-[14px] font-medium uppercase tracking-[1.4px] text-text-muted">
              {eyebrow}
            </span>
          </div>
        )}
        <h1 className="font-display text-[40px] leading-[44px] font-semibold tracking-[-0.5px] text-text-primary">
          {title.split(" ").map((word, i) => {
            const isAccent = word.toLowerCase() === "questions" || word.toLowerCase() === "voulez";
            return (
              <span key={i} className={isAccent ? "text-secondary-800" : undefined}>
                {word}
                {i < title.split(" ").length - 1 ? " " : ""}
              </span>
            );
          })}
        </h1>
      </section>

      <section className="mx-auto w-full max-w-[832px] px-6 pb-24 md:px-0">
        {data?.items?.length > 0 && <JsonLd data={faqLd(data.items)} />}
        {/* Native <details> rather than a JS accordion: every answer ships in the
            server-rendered HTML, so engines that don't run JavaScript can quote it.
            The shared name= gives the one-open-at-a-time behaviour for free. */}
        <div className="flex flex-col gap-4">
          {data?.items?.map((item: any) => (
            <details
              key={item._id}
              name="faq"
              className="faq-item group overflow-hidden rounded-[40px] bg-surface-warm shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
            >
              {/* The h2 sits inside <summary> — allowed, and it keeps each question
                  in the heading outline under the page h1. */}
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-6 text-left text-text-primary [&::-webkit-details-marker]:hidden">
                <h2 className="text-h3 m-0">{pickLocalized(item.question, locale)}</h2>
                <span className="relative flex size-5 shrink-0 items-center justify-center text-text-primary">
                  <Plus className="size-5 group-open:hidden" strokeWidth={2} />
                  <Minus className="hidden size-5 group-open:block" strokeWidth={2} />
                </span>
              </summary>
              <div className="px-6 pb-6 pt-0 font-sans text-[17px] leading-[26px] text-text-secondary">
                {pickLocalized(item.answer, locale)}
              </div>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}